import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import Alert from "react-bootstrap/Alert";
import CloseButton from "react-bootstrap/CloseButton";
import Form from "react-bootstrap/Form";
import { useEffect, useState } from "react";
import { useAppDispatch } from "src/hooks/redux";
import { submitAddendumOnBehalf } from "../../services/AdminDashService";
import {
  ApplicationAddendum,
  ApplicationAddendumQuestion,
  ApplicationAddendumSubmission,
} from "src/types/LeaseDomainAddendum";

interface SubmitAddendumOnBehalfModalProps {
  show: boolean;
  onHide: () => void;
  leaseId: string;
  applicantId: string;
  applicantName: string;
  // Every pending addendum for the application - they are all answered and submitted together.
  addendums: ApplicationAddendum[];
  onSubmitted: () => void;
}

const isFreeText = (question: ApplicationAddendumQuestion) =>
  question.questionType === "text" || question.questionType === "single-line";
const isSingleChoice = (question: ApplicationAddendumQuestion) =>
  question.questionType === "single-select" ||
  question.questionType === "boolean";
const isMultiSelect = (question: ApplicationAddendumQuestion) =>
  question.questionType === "multi-select";

const sortQuestions = (addendum: ApplicationAddendum) =>
  [...addendum.formQuestions].sort((a, b) => a.sortOrder - b.sortOrder);

// Answers are keyed per addendum so two forms can never share state through a question id.
const answerKey = (addendumId: string, questionId: string) =>
  `${addendumId}:${questionId}`;

const SubmitAddendumOnBehalfModal = ({
  show,
  onHide,
  leaseId,
  applicantId,
  applicantName,
  addendums,
  onSubmitted,
}: SubmitAddendumOnBehalfModalProps) => {
  const dispatch = useAppDispatch();
  const [textValues, setTextValues] = useState<Record<string, string>>({});
  const [selectedAnswerIds, setSelectedAnswerIds] = useState<
    Record<string, string[]>
  >({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Reset only on the show:false->true transition, not on every `addendums` identity change -
  // Applicants.tsx passes a freshly-derived array on every render, so keying on it too would
  // wipe in-progress answers on any unrelated parent re-render while the modal is open.
  useEffect(() => {
    if (!show) return;
    setTextValues({});
    setSelectedAnswerIds({});
    setErrorMessage(null);
  }, [show]);

  const unsupportedQuestion = addendums
    .flatMap((addendum) => addendum.formQuestions)
    .find(
      (question) =>
        !isFreeText(question) &&
        !isSingleChoice(question) &&
        !isMultiSelect(question)
    );

  const toggleAnswer = (key: string, answerId: string, checked: boolean) => {
    setSelectedAnswerIds((prev) => {
      const current = prev[key] ?? [];
      return {
        ...prev,
        [key]: checked
          ? [...current, answerId]
          : current.filter((id) => id !== answerId),
      };
    });
  };

  // Multi-select is required only through minimumAnswers, so an optional checkbox form (Stove
  // Knob) can be submitted with nothing ticked - matching Lease Domain's own rule.
  const validate = (): string | null => {
    for (const addendum of addendums) {
      for (const question of sortQuestions(addendum)) {
        const key = answerKey(addendum.addendumId, question.formQuestionId);
        if (isFreeText(question)) {
          if (question.isRequired && !(textValues[key] ?? "").trim()) {
            return "Please answer all required questions.";
          }
          continue;
        }
        const selected = selectedAnswerIds[key] ?? [];
        if (isMultiSelect(question)) {
          const minimumAnswers = question.minimumAnswers ?? 0;
          if (minimumAnswers > 0 && selected.length < minimumAnswers) {
            return `Please select at least ${minimumAnswers} option(s) for "${question.questionText}".`;
          }
          continue;
        }
        if (question.isRequired && selected.length === 0) {
          return "Please answer all required questions.";
        }
      }
    }
    return null;
  };

  // Options post their own answerText, not their id - Lease Domain matches on answerText, the
  // same shape the resident-facing UI submits. Forms with nothing answered are omitted.
  const buildAddenda = (): ApplicationAddendumSubmission[] =>
    addendums.flatMap((addendum) => {
      const answers = sortQuestions(addendum).flatMap((question) => {
        const key = answerKey(addendum.addendumId, question.formQuestionId);
        if (isFreeText(question)) {
          const value = (textValues[key] ?? "").trim();
          return value
            ? [{ questionId: question.formQuestionId, answerValue: value }]
            : [];
        }
        return (selectedAnswerIds[key] ?? []).flatMap((answerId) => {
          const option = question.formQuestionAnswers.find(
            (answer) => answer.answerId === answerId
          );
          return option
            ? [
                {
                  questionId: question.formQuestionId,
                  answerValue: option.answerText,
                },
              ]
            : [];
        });
      });
      return answers.length
        ? [{ addendumId: addendum.addendumId, answers }]
        : [];
    });

  const handleSubmit = async () => {
    if (addendums.length === 0 || unsupportedQuestion) return;
    const validationError = validate();
    if (validationError) {
      setErrorMessage(validationError);
      return;
    }

    setIsSubmitting(true);
    setErrorMessage(null);
    const result = await dispatch(
      submitAddendumOnBehalf(leaseId, applicantId, buildAddenda())
    );
    setIsSubmitting(false);

    // Someone else submitting first still leaves the addenda complete, so refresh either way.
    if (result !== "failed") {
      onSubmitted();
      onHide();
    }
  };

  const renderQuestion = (
    addendum: ApplicationAddendum,
    question: ApplicationAddendumQuestion
  ) => {
    const key = answerKey(addendum.addendumId, question.formQuestionId);
    const inputId = (answerId: string) =>
      `addendum-${addendum.addendumId}-question-${question.formQuestionId}-answer-${answerId}`;
    const sortedAnswers = [...question.formQuestionAnswers].sort(
      (a, b) => a.sortOrder - b.sortOrder
    );

    if (isFreeText(question)) {
      return (
        <Form.Control
          type="text"
          aria-label={question.questionText}
          value={textValues[key] ?? ""}
          onChange={(event) =>
            setTextValues((prev) => ({ ...prev, [key]: event.target.value }))
          }
        />
      );
    }
    if (isSingleChoice(question)) {
      return sortedAnswers.map((answer) => (
        <Form.Check
          key={answer.answerId}
          type="radio"
          name={`question-${key}`}
          id={inputId(answer.answerId)}
          label={answer.answerText}
          checked={(selectedAnswerIds[key] ?? [])[0] === answer.answerId}
          onChange={() =>
            setSelectedAnswerIds((prev) => ({
              ...prev,
              [key]: [answer.answerId],
            }))
          }
        />
      ));
    }
    if (isMultiSelect(question)) {
      return sortedAnswers.map((answer) => (
        <Form.Check
          key={answer.answerId}
          type="checkbox"
          id={inputId(answer.answerId)}
          label={answer.answerText}
          checked={(selectedAnswerIds[key] ?? []).includes(answer.answerId)}
          onChange={(event) =>
            toggleAnswer(key, answer.answerId, event.target.checked)
          }
        />
      ));
    }
    return (
      <Alert variant="warning">
        Unsupported question type &quot;{question.questionType}&quot;.
      </Alert>
    );
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      aria-labelledby="submit-addendum-on-behalf-modal"
    >
      <Modal.Header className="header">
        <Modal.Title className="title" id="submit-addendum-on-behalf-modal">
          Answer Addendum on Behalf of {applicantName}
        </Modal.Title>
        <CloseButton onClick={onHide} />
      </Modal.Header>
      <Modal.Body>
        <p>
          Only one applicant needs to make these selections. Once submitted,
          this information <b>cannot</b> be altered.
        </p>

        {unsupportedQuestion && (
          <Alert variant="warning">
            An addendum has a question type this form doesn&apos;t support yet
            (&quot;{unsupportedQuestion.questionType}&quot;) - the addenda
            can&apos;t be submitted here.
          </Alert>
        )}

        {addendums.map((addendum) => (
          <section className="mb-4" key={addendum.addendumId}>
            <h5>{addendum.formTitle ?? addendum.addendumId}</h5>
            {addendum.formDisclaimer && (
              <p className="text-muted">{addendum.formDisclaimer}</p>
            )}
            {sortQuestions(addendum).map((question) => (
              <Form.Group className="mb-3" key={question.formQuestionId}>
                <Form.Label>{question.questionText}</Form.Label>
                {renderQuestion(addendum, question)}
              </Form.Group>
            ))}
          </section>
        ))}

        {errorMessage && <Alert variant="danger">{errorMessage}</Alert>}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="light" onClick={onHide} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleSubmit}
          disabled={
            isSubmitting || addendums.length === 0 || !!unsupportedQuestion
          }
        >
          {isSubmitting ? "Submitting..." : "Submit"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default SubmitAddendumOnBehalfModal;
